import { ReactNode, useCallback, useMemo, useState } from 'react';

export type ColumnDef<T> = {
  field?: keyof T;
  header: string;
  renderCell?: (record: T, colDef: ColumnDef<T>, rowIndex: number) => ReactNode;
};

export const useTable = <T>({ colDefs, data }: { colDefs: ColumnDef<T>[]; data: T[] }) => {
  const [sortModel, setSortModel] = useState<{
    field: keyof T | '';
    direction: 'asc' | 'desc';
  }>({
    field: '',
    direction: 'asc',
  });

  const tableHeaders = useMemo(
    () =>
      colDefs.map((colDef) => ({
        header: colDef.header,
        sort: sortModel.field === colDef.field ? sortModel.direction : null,
      })),
    [colDefs, sortModel],
  );

  const tableData = useMemo(() => {
    let sortedData = data;

    if (sortModel.field) {
      sortedData = sortTableData(data, sortModel.field, sortModel.direction);
    }

    return sortedData.map((record, index) =>
      colDefs.map<ReactNode>((colDef) => {
        if (colDef.renderCell) return colDef.renderCell(record, colDef, index);
        if (colDef.field) return String(record[colDef.field as keyof T]);
        return null;
      }),
    );
  }, [colDefs, data, sortModel]);

  const sortedData = useMemo(() => {
    let sortedData = data;

    if (sortModel.field) {
      sortedData = sortTableData(data, sortModel.field, sortModel.direction);
    }

    return sortedData;
  }, [data, sortModel]);

  const onHeaderClick = useCallback(
    (index: number) => {
      const field = colDefs[index].field;
      if (!field) return;

      setSortModel((prev) => {
        if (prev.field !== field) {
          return {
            field,
            direction: 'asc',
          };
        }

        switch (prev.direction) {
          case 'asc':
            return {
              field,
              direction: 'desc',
            };
          case 'desc':
            return {
              field: '',
              direction: 'asc',
            };
          default:
            return {
              field,
              direction: 'asc',
            };
        }
      });
    },
    [colDefs],
  );

  return { tableHeaders, tableData, sortedData, onHeaderClick };
};

const sortTableData = <T>(data: T[], field: keyof T, direction: 'asc' | 'desc') => {
  return [...data].sort((d1, d2) => {
    const a = d1[field];
    const b = d2[field];
    if (typeof a === 'number' && typeof b === 'number') {
      return direction === 'asc' ? a - b : b - a; // Numeric sorting
    } else {
      const result = String(a).localeCompare(String(b), undefined, { numeric: true });
      return direction === 'asc' ? result : -result; // String sorting
    }
  });
};
